/**
 * 邮件模板管理页面组件。
 *
 * 该文件实现了 CRM 系统中的「邮件模板管理」功能页面，包括模板的增删改查、
 * 富文本编辑（支持内联图片上传）、附件管理、模板分类管理，以及模板和快捷片段两种类型的切换。
 *
 * 主要导出内容：
 * - `CrmEmailTemplatesPage` — 注册为 `<crm-email-templates-page>` 自定义元素的 Lit 组件类
 *
 * 与其他模块的关系：
 * - 通过 `../api.js` 调用 `emailTemplateApi`、`emailTemplateCategoryApi`、`uploadTemplateAttachment`、`uploadTemplateInlineImage` 等 API
 * - 使用 `../components/rich-editor.js` 富文本编辑器组件（支持自定义图片上传器）
 * - 依赖 `../components/` 下的公共组件（table-toolbar、loading-overlay、select、pagination、toast、confirm-dialog）
 * - 使用 `../utils/index.js` 的 `isNetworkError` 和 `formatFileSize` 工具函数
 *
 * 关键设计决策：
 * - 模板分两种类型（type）：`template`（完整邮件模板，含主题行）和 `snippet`（快捷片段，无主题行），通过 Tab 切换
 * - 富文本编辑器支持内联图片上传：通过 `customImageUploader` 回调将图片上传后返回 `cid:` 引用，
 *   并在编辑时将 `cid:` 替换为可访问的图片 URL（携带 token 鉴权）
 * - 模板分类支持全局/私有的区分，内联编辑模式
 * - 搜索采用 300ms 防抖策略，避免频繁请求
 * - 附件上传有数量上限（3 个）和单文件大小限制（20MB）
 */
import { css, html } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { emailTemplateApi, emailTemplateCategoryApi, uploadTemplateAttachment, uploadTemplateInlineImage, API_BASE_URL } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { EmailTemplate, EmailTemplateCategory, TemplateAttachment, CreateEmailTemplateRequest, UpdateEmailTemplateRequest } from '../types.js';
import { toast } from '../components/toast.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/rich-editor.js';
import '../components/select.js';
import '../components/pagination.js';
import { showConfirm } from '../components/confirm-dialog.js';
import type { CrmRichEditor } from '../components/rich-editor.js';
import { isNetworkError, formatFileSize } from '../utils/index.js';

/** 每个模板最多允许的附件数量 */
const MAX_ATTACHMENTS = 3;
/** 单个附件文件大小上限（20MB，以字节为单位） */
const MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * 邮件模板管理页面组件。
 *
 * 职责：
 * - 展示邮件模板/快捷片段列表（分页表格），支持按关键词搜索和分类筛选
 * - 新增/编辑模板（弹窗），包含模板名称、分类、主题、内容类型、状态、变量 JSON、附件管理
 * - 使用富文本编辑器编辑模板内容，支持内联图片上传
 * - 模板分类的增删改（弹窗内联编辑）
 * - 支持在「邮件模板」和「快捷片段」之间切换
 *
 * 该组件注册为 `<crm-email-templates-page>` 自定义元素。
 */
@customElement('crm-email-templates-page')
export class CrmEmailTemplatesPage extends BaseElement {
  /** 邮件模板/片段列表数据 */
  @state() private templates: EmailTemplate[] = [];
  /** 列表加载状态标志 */
  @state() private isLoading = false;
  /** 列表数据总条数（用于分页） */
  @state() private total = 0;
  /** 当前页码 */
  @state() private currentPage = 1;
  /** 每页显示条数 */
  @state() private perPage = 20;
  /** 是否显示模板编辑弹窗 */
  @state() private showModal = false;
  /** 正在编辑的模板对象（null 表示新增模式） */
  @state() private editingTemplate: EmailTemplate | null = null;
  /** 表单 - 模板内容类型：html / plain */
  @state() private formTemplateType = 'html';
  /** 表单 - 状态值（字符串形式）：'1'=启用 / '0'=禁用 */
  @state() private formStatus = '1';
  /** 表单 - 选中的分类 ID（0 表示无分类） */
  @state() private formCategoryId = 0;
  /** 已上传的附件列表 */
  @state() private attachedFiles: TemplateAttachment[] = [];
  /** 附件上传中状态标志 */
  @state() private uploadingAttachment = false;

  /** 模板分类列表 */
  @state() private categories: EmailTemplateCategory[] = [];
  /** 是否显示分类管理弹窗 */
  @state() private showCategoryModal = false;
  /** 分类管理 - 新建分类名称输入值 */
  @state() private newCategoryName = '';
  /** 分类管理 - 正在编辑的分类 ID（null 表示未在编辑） */
  @state() private editingCategoryId: number | null = null;
  /** 分类管理 - 编辑中的分类名称 */
  @state() private editingCategoryName = '';

  /** 搜索关键词（已提交的值，用于 API 请求） */
  @state() private filterKeyword = '';
  /** 分类筛选 ID（null 表示全部分类） */
  @state() private filterCategoryId: number | null = null;
  /** 当前激活的标签页：'template'=邮件模板 / 'snippet'=快捷片段 */
  @state() private currentTab: string = 'template';
  /** 搜索防抖定时器 ID */
  private _searchTimer: number | null = null;
  /** 列表请求序号，仅允许最后一次筛选请求更新页面。 */
  private _listRequestId = 0;
  /** 标记鼠标是否在遮罩层上按下（用于弹窗的点击遮罩关闭） */
  private _backdropMousedown = false;

  /** 通过 @query 装饰器获取的富文本编辑器组件实例引用 */
  @query('crm-rich-editor') private richEditor!: CrmRichEditor;

  static styles = css`
    :host { display: block; height: 100%; }
    .attachment-item { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #f0f0f3; border-radius: 4px; font-size: 13px; }
    .attachment-item .name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #1c2024; }
    .attachment-item .size { color: #60646c; white-space: nowrap; }
    .attachment-item .remove { color: #eb8e90; cursor: pointer; padding: 2px 6px; border-radius: 3px; background: none; border: none; font-size: 16px; line-height: 1; }
    .attachment-item .remove:hover { background: #eb8e9018; }
    .drop-zone { border: 1px dashed #d9d9e0; border-radius: 4px; padding: 10px; text-align: center; color: #60646c; font-size: 13px; transition: border-color 0.15s; }
    .drop-zone:hover { border-color: #0d74ce; }
    .drop-zone.full { opacity: 0.5; pointer-events: none; }
  `;

  /**
   * 组件挂载时的生命周期回调。
   *
   * 调用父类方法后：
   * 1. 加载分类列表
   * 2. 加载模板列表
   * 3. 在 document 上注册 ESC 键监听（用于关闭编辑弹窗）
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadCategories();
    this.loadTemplates();
    document.addEventListener('keydown', this.handleEscKey);
  }

  /**
   * 组件卸载时的生命周期回调。
   *
   * 调用父类方法后：
   * 1. 移除 document 上的 ESC 键监听
   * 2. 清理搜索防抖定时器
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscKey);
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  /**
   * ESC 键监听回调（箭头函数，确保 this 绑定正确）。
   *
   * 仅在编辑弹窗打开时响应 ESC 键关闭弹窗。
   *
   * @param e - 键盘事件对象
   */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.showModal) this.closeModal();
  };

  /**
   * 加载模板分类列表。
   *
   * 从后端获取全部分类数据，存入 categories 状态。
   */
  private async loadCategories() {
    try {
      const res = await emailTemplateCategoryApi.list();
      this.categories = res.data;
    } catch {
      console.error('Failed to load categories');
    }
  }

  /**
   * 加载模板/片段列表数据。
   *
   * 组装查询参数（页码、每页条数、关键词、分类筛选、当前 Tab 类型），
   * 从后端获取列表数据并更新 templates、total、currentPage、perPage 状态。
   * 加载期间设置 isLoading 为 true 以显示加载遮罩。
   */
  private async loadTemplates() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const params: Record<string, any> = { page: this.currentPage, per_page: this.perPage };
      if (this.filterKeyword) params.keyword = this.filterKeyword;
      if (this.filterCategoryId !== null) params.category_id = this.filterCategoryId;
      // currentTab 决定加载的是「邮件模板」还是「快捷片段」
      params.type = this.currentTab;
      const response = await emailTemplateApi.list(params);
      if (requestId !== this._listRequestId) return;
      this.templates = response.data;
      this.total = response.pagination.total;
      this.currentPage = response.pagination.current_page;
      this.perPage = response.pagination.per_page;
    } catch {
      if (requestId === this._listRequestId) console.error('Failed to load templates');
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 搜索输入处理（带防抖）。
   *
   * 用户输入时清除上一次的定时器并重新计时，
   * 300ms 后如果用户停止输入则提交搜索关键词并重置页码为 1。
   *
   * @param value - 搜索框输入值
   */
  private handleSearch(value: string) {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = window.setTimeout(() => {
      this.filterKeyword = value;
      this.currentPage = 1;
      this.loadTemplates();
    }, 300);
  }

  /**
   * 分类筛选处理。
   *
   * @param categoryId - 分类 ID；null 表示清除分类筛选
   */
  private handleCategoryFilter(categoryId: number | null) {
    this.filterCategoryId = categoryId;
    this.currentPage = 1;
    this.loadTemplates();
  }

  /**
   * 列表翻页处理。
   *
   * @param page - 目标页码
   */
  private handlePageChange(page: number) { this.currentPage = page; this.loadTemplates(); }

  /**
   * Tab 标签页切换处理。
   *
   * 切换当前 Tab（邮件模板 / 快捷片段），重置页码后重新加载列表。
   *
   * @param tab - 目标标签页：'template' / 'snippet'
   */
  private handleTabSwitch(tab: string) {
    this.currentTab = tab;
    this.currentPage = 1;
    this.loadTemplates();
  }

  /**
   * 打开新建模板弹窗。
   *
   * 重置编辑状态为新增模式，清空所有表单字段。
   */
  private openCreateModal() {
    this.editingTemplate = null;
    this.formTemplateType = 'html';
    this.formStatus = '1';
    this.formCategoryId = 0;
    this.attachedFiles = [];
    this.showModal = true;
  }

  /**
   * 获取内联图片的可访问 URL。
   *
   * 后端内联图片接口使用 HttpOnly Cookie 鉴权，URL 本身不包含敏感令牌。
   *
   * @param imageId - 内联图片 ID
   * @returns 内联图片 URL
   */
  private getImageUrl(imageId: number): string {
    return `${API_BASE_URL}/email-templates/inline-images/${imageId}/file`;
  }

  /**
   * 将模板内容中的 cid: 引用替换为可访问的图片 URL。
   *
   * 后端存储的模板内容中使用 `cid:xxx` 引用内联图片，
   * 在编辑器中显示时需要替换为使用 Cookie 鉴权的实际 URL。
   * 对 cid 中的正则特殊字符进行转义，确保安全替换。
   *
   * @param content - 原始模板内容（HTML）
   * @param inlineImages - 内联图片列表，包含 id 和 cid 字段
   * @returns 替换后的模板内容（HTML 中 cid: 已替换为实际 URL）
   */
  private resolveCidsToUrls(content: string, inlineImages: Array<{ id: number; cid: string }>): string {
    if (!content || !inlineImages?.length) return content || '';
    let result = content;
    for (const img of inlineImages) {
      if (img.cid) {
        // 转义 cid 中的正则特殊字符，防止注入
        const escaped = img.cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        result = result.replace(new RegExp(`cid:${escaped}`, 'g'), this.getImageUrl(img.id));
      }
    }
    return result;
  }

  /**
   * 打开编辑模板弹窗。
   *
   * 将模板内容中的 cid: 引用替换为实际 URL 后存入 editingTemplate，
   * 设置表单字段值，等待 DOM 更新完成后加载附件列表。
   *
   * @param t - 要编辑的模板对象
   */
  private async openEditModal(t: EmailTemplate) {
    this.editingTemplate = {
      ...t,
      // 将 cid: 引用替换为实际 URL，以便编辑器正确显示图片
      content: this.resolveCidsToUrls(t.content, t.inline_images || []),
    };
    this.formTemplateType = t.template_type || 'html';
    this.formStatus = String(t.status ?? 1);
    this.formCategoryId = t.category_id || 0;
    this.showModal = true;
    // 等待 DOM 更新完成后再加载附件列表
    await this.updateComplete;
    if (t.attachments) this.attachedFiles = [...t.attachments];
  }

  /**
   * 关闭编辑弹窗并重置状态（箭头函数，确保 this 绑定）。
   *
   * 清空 editingTemplate 和 attachedFiles。
   */
  private closeModal = () => {
    this.showModal = false;
    this.editingTemplate = null;
    this.attachedFiles = [];
  };

  /**
   * 富文本编辑器自定义图片上传回调（箭头函数，确保 this 绑定）。
   *
   * 当用户在富文本编辑器中插入图片时被调用：
   * 1. 调用 uploadTemplateInlineImage 上传图片到后端
   * 2. 返回 `cid:` 引用和可访问的显示 URL
   * 3. 编辑器将 cid: 插入到内容中，同时用 displayUrl 显示预览
   *
   * @param file - 用户选择的图片文件
   * @returns 包含 cid 和 displayUrl 的对象
   */
  private handleImageUpload = async (file: File): Promise<{ cid: string; displayUrl: string }> => {
    const result = await uploadTemplateInlineImage(file);
    return { cid: `cid:${result.data.cid}`, displayUrl: this.getImageUrl(result.data.id) };
  };

  /**
   * 添加附件。
   *
   * 动态创建 file input 元素，用户选择文件后逐个上传：
   * 1. 检查剩余可上传数量（MAX_ATTACHMENTS - 已有附件数）
   * 2. 检查单文件大小限制（MAX_FILE_SIZE = 20MB）
   * 3. 调用 uploadTemplateAttachment 上传
   * 4. 上传成功后添加到 attachedFiles 列表
   *
   * 超出数量限制会提示错误，超过大小限制的文件会跳过并提示。
   */
  private async addAttachment() {
    // 动态创建 file input，不依赖 DOM 中的固定元素
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      // 计算剩余可上传数量
      const remaining = MAX_ATTACHMENTS - this.attachedFiles.length;
      if (files.length > remaining) {
        toast.error(`最多还能添加 ${remaining} 个附件`);
        return;
      }
      for (const f of files) {
        // 校验单文件大小
        if (f.size > MAX_FILE_SIZE) {
          toast.error(`"${f.name}" 超过 20MB 限制`);
          continue;
        }
        this.uploadingAttachment = true;
        try {
          const r = await uploadTemplateAttachment(f);
          this.attachedFiles = [...this.attachedFiles, {
            id: r.data.id,
            template_id: 0,
            filename: f.name,
            content_type: f.type || 'application/octet-stream',
            file_size: f.size,
            created_at: Math.floor(Date.now() / 1000),
          }];
        } catch (err: any) {
          if (!isNetworkError(err.message)) toast.error(err.message || '上传失败');
        } finally {
          this.uploadingAttachment = false;
        }
      }
    };
    input.click();
  }

  /**
   * 删除指定附件。
   *
   * 弹出确认对话框，确认后调用后端 API 删除附件并从列表中移除。
   *
   * @param att - 要删除的附件对象
   */
  private async removeAttachment(att: TemplateAttachment) {
    if (!(await showConfirm(`确定要删除附件 "${att.filename}" 吗？`))) return;
    try {
      await emailTemplateApi.deleteAttachment(att.id);
      this.attachedFiles = this.attachedFiles.filter(a => a.id !== att.id);
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 处理模板表单提交（新增或更新）。
   *
   * 从表单收集名称、主题、变量等字段，从富文本编辑器获取内容，
   * 从编辑器导出内联图片 ID 列表，组装请求数据后调用 API。
   *
   * @param e - 表单提交事件对象
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    // 从富文本编辑器获取内容和内联图片 ID
    const editor = this.richEditor;
    const content = editor?.value || '';

    // 组装请求数据
    const data: Record<string, any> = {
      name: formData.get('name') as string,
      subject: formData.get('subject') as string,
      content,
      template_type: this.formTemplateType,
      type: this.currentTab,
      variables: formData.get('variables') as string,
      status: this.formStatus === '1' ? 1 : 0,
      category_id: this.formCategoryId,
      attachment_ids: this.attachedFiles.map(a => a.id),
      // 导出编辑器中引用的内联图片 ID，用于后端关联
      inline_image_ids: editor?.exportInlineImageIds() || [],
    };

    try {
      if (this.editingTemplate) {
        await emailTemplateApi.update(this.editingTemplate.id, data as UpdateEmailTemplateRequest);
        toast.success('模板更新成功');
      } else {
        await emailTemplateApi.create(data as CreateEmailTemplateRequest);
        toast.success('模板创建成功');
      }
      await this.loadTemplates();
      this.closeModal();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '操作失败');
    }
  }

  /**
   * 删除指定模板。
   *
   * 弹出确认对话框，确认后调用 API 删除模板并重新加载列表。
   *
   * @param id - 要删除的模板 ID
   */
  private async deleteTemplate(id: number) {
    if (!(await showConfirm('确定要删除此模板吗？'))) return;
    try {
      await emailTemplateApi.delete(id);
      toast.success('模板删除成功');
      await this.loadTemplates();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  // ========== 分类管理 ==========

  /**
   * 创建新的模板分类。
   *
   * 从 newCategoryName 获取名称，去空格后调用后端创建 API。
   * 成功后清空输入框并重新加载分类列表。
   */
  private async createCategory() {
    const name = this.newCategoryName.trim();
    if (!name) return;
    try {
      await emailTemplateCategoryApi.create(name);
      this.newCategoryName = '';
      await this.loadCategories();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '创建失败');
    }
  }

  /**
   * 进入分类内联编辑模式。
   *
   * 设置 editingCategoryId 和 editingCategoryName，使对应分类行显示编辑输入框。
   *
   * @param cat - 要编辑的分类对象
   */
  private startEditCategory(cat: EmailTemplateCategory) {
    this.editingCategoryId = cat.id;
    this.editingCategoryName = cat.name;
  }

  /**
   * 保存分类名称修改。
   *
   * 调用后端更新 API，成功后退出编辑模式并重新加载分类列表。
   */
  private async saveCategory() {
    if (!this.editingCategoryId) return;
    const name = this.editingCategoryName.trim();
    if (!name) return;
    try {
      await emailTemplateCategoryApi.update(this.editingCategoryId, name);
      this.editingCategoryId = null;
      this.editingCategoryName = '';
      await this.loadCategories();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '更新失败');
    }
  }

  /**
   * 取消分类编辑模式。
   *
   * 清空 editingCategoryId 和 editingCategoryName，恢复到展示模式。
   */
  private cancelEditCategory() {
    this.editingCategoryId = null;
    this.editingCategoryName = '';
  }

  /**
   * 删除指定分类。
   *
   * 弹出确认对话框，确认后调用后端删除 API。
   * 如果当前筛选的正是该分类，清除筛选并重新加载模板列表。
   *
   * @param cat - 要删除的分类对象
   */
  private async deleteCategory(cat: EmailTemplateCategory) {
    if (!(await showConfirm(`确定要删除分类"${cat.name}"吗？`))) return;
    try {
      await emailTemplateCategoryApi.delete(cat.id);
      toast.success('分类删除成功');
      await this.loadCategories();
      // 如果当前筛选的分类被删除，清除筛选条件并重新加载
      if (this.filterCategoryId === cat.id) {
        this.filterCategoryId = null;
        this.loadTemplates();
      }
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 渲染附件管理区块。
   *
   * 仅在存在附件或处于编辑模式时渲染。包含：
   * - 已上传附件列表：每项显示文件图标、文件名、文件大小和删除按钮
   * - 添加附件按钮：未达上限（MAX_ATTACHMENTS）时显示，点击触发 addAttachment
   * - 单文件大小限制提示（20MB）
   *
   * @returns Lit html 模板；无附件且非编辑模式时返回空字符串
   */
  private renderAttachments() {
    if (this.attachedFiles.length === 0 && !this.editingTemplate) return '';

    return html`
      <div>
        <label class="block mb-1.5 text-sm font-medium text-text">附件（${this.attachedFiles.length}/${MAX_ATTACHMENTS}）</label>
        ${this.attachedFiles.length > 0 ? html`
          <div class="flex flex-col gap-1.5 mb-2">
            ${this.attachedFiles.map(att => html`
              <div class="attachment-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14" style="color:#60646c;flex-shrink:0;"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                <span class="name">${att.filename}</span>
                <span class="size">${formatFileSize(att.file_size)}</span>
                <button class="remove" @click=${() => this.removeAttachment(att)} title="删除附件">×</button>
              </div>
            `)}
          </div>
        ` : ''}
        ${this.attachedFiles.length < MAX_ATTACHMENTS ? html`
          <div class="drop-zone">
            <button type="button" class="text-primary hover:text-primary-hover text-sm cursor-pointer bg-transparent border-none p-0" @click=${this.addAttachment} ?disabled=${this.uploadingAttachment}>
              ${this.uploadingAttachment ? '上传中...' : '+ 添加附件'}
            </button>
            <span class="text-xs ml-1">单个文件不超过 20MB</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * 通用弹窗渲染方法。
   *
   * 接收标题、内容和关闭回调，渲染一个带遮罩的居中弹窗。
   * 使用函数局部变量 `backdropMousedown` 实现点击遮罩关闭（防止拖拽误触）。
   *
   * @param title - 弹窗标题
   * @param body - 弹窗内容（Lit html 模板）
   * @param onClose - 关闭弹窗的回调函数
   * @returns Lit html 模板，包含遮罩层、弹窗容器、标题栏和内容区
   */
  private renderModal(title: string, body: unknown, onClose: () => void) {
    let backdropMousedown = false;
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (backdropMousedown) onClose(); }}>
        <div class="bg-surface rounded-md shadow-xl border border-border mx-4 flex flex-col" style="width:80vw;height:80vh;" @click=${(e: Event) => e.stopPropagation()}>
          <div class="flex justify-between items-center px-6 pt-4 pb-3 border-b border-border flex-shrink-0">
            <h2 class="m-0 font-semibold text-lg text-text">${title}</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="flex-1 min-h-0">${body}</div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染分类管理弹窗。
   *
   * 弹窗包含：
   * - 新建分类输入框和「添加」按钮（支持回车提交）
   * - 现有分类列表：每行显示分类名称、全局/私有标签、编辑和删除按钮
   * - 编辑模式下显示内联输入框，支持回车保存和 ESC 取消
   *
   * @returns Lit html 模板；showCategoryModal 为 false 时返回空字符串
   */
  private renderCategoryModal() {
    if (!this.showCategoryModal) return '';
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) { this.showCategoryModal = false; } }}>
        <div class="bg-surface rounded-md shadow-xl border border-border mx-4 w-full max-w-md" @click=${(e: Event) => e.stopPropagation()}>
          <div class="flex justify-between items-center px-6 pt-4 pb-3 border-b border-border">
            <h2 class="m-0 font-semibold text-lg text-text">分类管理</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${() => { this.showCategoryModal = false; }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-5">
            <div class="flex gap-2 mb-4">
              <input type="text" placeholder="新分类名称" maxlength="64"
                class="flex-1 px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:outline-none"
                .value=${this.newCategoryName}
                @input=${(e: Event) => { this.newCategoryName = (e.target as HTMLInputElement).value; }}
                @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.createCategory(); }}
              />
              <button class="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer" @click=${this.createCategory}>添加</button>
            </div>
            <div class="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
              ${this.categories.length === 0 ? html`<p class="text-sm text-text-muted text-center py-4">暂无分类</p>` :
                this.categories.map(cat => html`
                  <div class="flex items-center gap-2 px-3 py-2 rounded bg-bg">
                    ${this.editingCategoryId === cat.id ? html`
                      <input type="text" class="flex-1 px-2 py-1 border border-primary rounded text-sm bg-surface text-text focus:outline-none"
                        .value=${this.editingCategoryName}
                        @input=${(e: Event) => { this.editingCategoryName = (e.target as HTMLInputElement).value; }}
                        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.saveCategory(); if (e.key === 'Escape') this.cancelEditCategory(); }}
                      />
                      <button class="text-xs text-primary cursor-pointer bg-transparent border-none font-medium" @click=${this.saveCategory}>保存</button>
                      <button class="text-xs text-text-muted cursor-pointer bg-transparent border-none" @click=${this.cancelEditCategory}>取消</button>
                    ` : html`
                      <span class="flex-1 text-sm text-text">${cat.name}</span>
                      <span class="text-xs px-1.5 py-0.5 rounded ${cat.is_global ? 'bg-info/10 text-info' : 'bg-bg text-text-muted'}">${cat.is_global ? '全局' : '私有'}</span>
                      <button class="text-xs text-primary cursor-pointer bg-transparent border-none" @click=${() => this.startEditCategory(cat)}>编辑</button>
                      <button class="text-xs text-error cursor-pointer bg-transparent border-none" @click=${() => this.deleteCategory(cat)}>删除</button>
                    `}
                  </div>
                `)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 主渲染方法。
   *
   * 页面结构自上而下：
   * 1. 顶部操作栏：标题、Tab 切换（邮件模板/快捷片段）、搜索框、分类筛选下拉、分类管理按钮、新增模板按钮、工具栏
   * 2. 模板列表表格：展示模板名称、分类、主题/内容摘要、类型、状态、操作（编辑/删除）
   *    - 主题列根据 currentTab 动态显示：template 模式显示主题，snippet 模式显示内容摘要（去 HTML 标签后截取前 60 字符）
   * 3. 分页组件
   * 4. 条件渲染的编辑弹窗（含左侧表单栏和右侧富文本编辑器）
   * 5. 条件渲染的分类管理弹窗
   *
   * @returns Lit html 模板
   */
  render() {
    // 列表筛选用的分类选项：第一项为「全部分类」
    const categoryOptions = [
      { label: '全部分类', value: '' },
      ...this.categories.map(c => ({ label: c.name, value: String(c.id) })),
    ];

    // 表单中用的分类选项：第一项为「无分类」，全局分类附加标注
    const formCategoryOptions = [
      { label: '无分类', value: '0' },
      ...this.categories.map(c => ({ label: `${c.name}${c.is_global ? ' (全局)' : ''}`, value: String(c.id) })),
    ];

    return html`
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-3 mb-3 flex-shrink-0 flex-wrap">
          <span class="text-base font-semibold text-text">邮件模板管理</span>
          <div class="flex-1"></div>
          <crm-select
            style="min-width: 120px"
            .value=${this.currentTab}
            .options=${[
              { label: "邮件模板", value: "template" },
              { label: "快捷片段", value: "snippet" },
            ]}
            @change=${(e: CustomEvent) => this.handleTabSwitch(e.detail.value)}
          ></crm-select>
          <input type="text" placeholder="搜索模板名称..."
            class="px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:outline-none w-48"
            @input=${(e: Event) => this.handleSearch((e.target as HTMLInputElement).value)}
          />
          <crm-select
            style="min-width: 150px"
            .value=${this.filterCategoryId === null ? '' : String(this.filterCategoryId)}
            .options=${categoryOptions}
            @change=${(e: CustomEvent) => {
              const v = e.detail.value;
              this.handleCategoryFilter(v === '' ? null : Number(v));
            }}
          ></crm-select>
          <button class="px-5 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${() => { this.showCategoryModal = true; }}>
            分类管理
          </button>
          <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-2" @click=${this.openCreateModal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>新增模板
          </button>
          <crm-table-toolbar .total=${this.total} .label=${'个模板'} .loading=${this.isLoading} @crm-toolbar-refresh=${() => this.loadTemplates()}></crm-table-toolbar>
        </div>

        <div class="flex-1 min-h-0 flex flex-col">
          <div class="bg-surface rounded-md shadow-lg flex flex-col flex-1 min-h-0 overflow-hidden relative">
            <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
            <div class="overflow-auto flex-1 min-h-0">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border">
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">模板名称</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">分类</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">${this.currentTab === 'template' ? '主题' : '内容摘要'}</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">类型</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">状态</th>
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  ${!this.isLoading && this.templates.length === 0
                    ? html`<tr><td colspan="6" class="text-center py-12 text-text-muted border-b border-border">暂无邮件模板</td></tr>`
                    : this.templates.map(t => html`
                      <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                        <td class="px-4 py-2 text-sm font-medium text-text">${t.name}</td>
                        <td class="px-4 py-2 text-sm text-text">${t.category_name || '-'}</td>
                        <!-- template 模式显示主题行；snippet 模式去除 HTML 标签后截取前 60 字符作为摘要 -->
                        <td class="px-4 py-2 text-sm text-text truncate max-w-xs">${this.currentTab === 'template' ? (t.subject || '-') : ((t.content || '').replace(/<[^>]*>/g, '').substring(0, 60) || '-')}</td>
                        <td class="px-4 py-2 text-sm text-text font-mono">${t.template_type}</td>
                        <td class="px-4 py-2">
                          ${t.status === 1
                            ? html`<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success">启用</span>`
                            : html`<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-error/10 text-error">禁用</span>`}
                        </td>
                        <td class="px-4 py-2 flex gap-1.5 flex-wrap">
                          <button class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.openEditModal(t)}>编辑</button>
                          <button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.deleteTemplate(t.id)}>删除</button>
                        </td>
                      </tr>
                    `)}
                </tbody>
              </table>
            </div>
            <crm-pagination
              .current=${this.currentPage}
              .perPage=${this.perPage}
              .total=${this.total}
              @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
            ></crm-pagination>
          </div>
        </div>
      </div>

      ${this.showModal ? this.renderModal(this.editingTemplate ? '编辑模板' : '新增模板', html`
        <form @submit=${this.handleSubmit} class="flex h-full">
          <div class="w-72 flex-shrink-0 border-r border-border overflow-y-auto p-5 flex flex-col gap-3">
            <div>
              <label class="block mb-1 text-sm font-medium text-text">模板名称 *</label>
              <input type="text" name="name" required maxlength="128" class="w-full px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingTemplate?.name || ''} />
            </div>
            <div>
              <crm-select label="分类" fullWidth .value=${String(this.formCategoryId)} .options=${formCategoryOptions} @change=${(e: CustomEvent) => { this.formCategoryId = Number(e.detail.value); }}></crm-select>
            </div>
            ${this.currentTab === 'template' ? html`
            <div>
              <label class="block mb-1 text-sm font-medium text-text">邮件主题</label>
              <input type="text" name="subject" maxlength="2048" class="w-full px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editingTemplate?.subject || ''} />
            </div>
            ` : html`<!-- snippet 模式无主题行，使用隐藏域占位 --><input type="hidden" name="subject" value="" />`}
            <div>
              <crm-select label="内容类型" fullWidth .value=${this.formTemplateType} .options=${[{ label: 'HTML', value: 'html' }, { label: '纯文本', value: 'plain' }]} @change=${(e: CustomEvent) => { this.formTemplateType = e.detail.value; }}></crm-select>
            </div>
            <div>
              <crm-select label="状态" fullWidth .value=${this.formStatus} .options=${[{ label: '启用', value: '1' }, { label: '禁用', value: '0' }]} @change=${(e: CustomEvent) => { this.formStatus = e.detail.value; }}></crm-select>
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-text">模板变量 (JSON)</label>
              <textarea name="variables" rows="3" class="w-full px-3 py-1.5 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all font-mono resize-none" .value=${this.editingTemplate?.variables || '{"name": "客户名称"}'}></textarea>
            </div>
            ${this.renderAttachments()}
            <div class="flex-1"></div>
            <div class="flex gap-2 pt-3 border-t border-border">
              <button type="button" class="flex-1 px-3 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer" @click=${this.closeModal}>取消</button>
              <button type="submit" class="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20">保存</button>
            </div>
          </div>
          <div class="flex-1 min-w-0 flex flex-col min-h-0">
            <div class="px-4 pt-4 pb-1 flex-shrink-0">
              <label class="block mb-1 text-sm font-medium text-text">邮件内容</label>
              <p class="text-xs text-text-muted">支持 HTML 和模板变量，如 <code class="bg-bg px-1 rounded">{{name}}</code></p>
            </div>
            <div class="flex-1 min-h-0 px-4 pb-4" style="display:flex;flex-direction:column;">
              <crm-rich-editor
                .value=${this.editingTemplate?.content || ''}
                .customImageUploader=${this.handleImageUpload}
              ></crm-rich-editor>
            </div>
          </div>
        </form>
      `, this.closeModal) : ''}

      ${this.renderCategoryModal()}
    `;
  }
}
